import { firstValueFrom } from 'rxjs'
import { inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { gte as semverGTE } from 'semver'

import { EditorMarker, Subclip, Tag } from 'elements-sdk'
import { LoggerFactory } from 'common/log'
import { Time } from 'media/time'
import { PremiereRenderModalComponent, SequenceRenderParams, SequenceRenderScope } from 'panel/components/premiereRenderSequenceModal.component'
import { AnyVariableType } from 'sdkSchemaClient'
import { GenericFileReader } from 'uploader/services/uploader.service'

// eslint-disable-next-line no-restricted-imports
import { CSInterface, SystemPath } from '../../../static/lib/js/CSInterface'
import { BaseInterface, loadAssetContentsPages } from './base'
import { NodeFileReader } from './common'

interface CEPWindow extends Window {
  cep?: AnyVariableType
  __adobe_cep__?: any
}

declare const window: CEPWindow

let require: any

/**
 * CEP (Common Extensibility Platform) is a system created by Adobe to allow developers
 * to build extensions and panels for their Creative Cloud apps (like Premiere Pro)
 * using web technologies.
 *
 * It's not the rendering engine itself, nor a runtime. It acts as a glue between the
 * HTML/CSS/JS of the panel and the host app's scripting interface (e.g. ExtendScript).
 * The rendering is handled by a modified version of Chromium Embedded Framework (CEF).
 * These modifications include extra things available in the global scope, notably:
 *
 *  - `CSInterface`, the main bridge to the host Adobe app (`evalScript`, `getSystemPath`, etc.),
 *  - `cep`, a namespace object with utility modules (`process`, `system`, `encoding`, etc.),
 *  - `__adobe_cep__`, a namespace object for lower-level access (`getExtensions`, etc.), and
 *  - Node.js-like shims (`require`, `process`, `Buffer`).
 */
export class CEPPanelInterface extends BaseInterface {
  override logger = inject(LoggerFactory).create('CEPPanelInterface')
  override native = true
  override uri = false
  override canUploadSequence = true
  override systemPath = SystemPath
  extensionServerPort = null
  currentImportFolder = ''

  private httpClient: HttpClient
  private cs: any
  private ngbModal: NgbModal

  constructor () {
    super()
    this.httpClient = inject(HttpClient)
    this.ngbModal = inject(NgbModal)

    this.cs = new CSInterface()
    this.app = this.getApp()
    this.platform = this.getPlatform()
  }

  async init () {
    this.logger.info('CEP init')

    require = window['nodeRequire'] = window['nodeRequire'] ?? window['require']

    // Make sure there's no interference with Webpack.
    window['global'] = undefined
    window['require'] = undefined
    window['module'] = undefined
    window['define'] = undefined
    window['exports'] = undefined

    this.cs.addEventListener(
      'com.elements.server.announcePort',
      (event) => {
        this.extensionServerPort = event.data
      },
    )

    this.cs.dispatchEvent({
      type: 'com.elements.extension.requestPort',
      scope: 'APPLICATION',
      appId: undefined,
      extensionId: undefined,
    })

    const scriptPrologue = `
      global = window = this;

      var babelHelpers = global.babelHelpers = {};
      babelHelpers['typeof'] = function (x) {
        return typeof x
      };
    `

    const response = await firstValueFrom(this.httpClient.get(`/static/build/extendscript.js?v=${window['cachebust']}`, { responseType: 'text' }))
    await this.evalScript(scriptPrologue + response)

    if (this.app === 'PPRO') {
      if (semverGTE(this.cs.getHostEnvironment().appVersion, '15.0.0')) {
        this.evalScript('app.premierePro = app.premiereProNWS')
      }
      this.evalScript('app.premierePro.init()')
    }

    this.active = true
  }

  getFS () {
    return window['cep']!['fs']
  }

  getApp () {
    return this.cs.getHostEnvironment().appId
  }

  getPlatform () {
    return this.cs.getOSInformation().indexOf('OS X') !== -1 ? 'osx' : 'win'
  }

  getSystemPath (name) {
    return this.cs.getSystemPath(name)
  }

  evalScript<R> (script, silent?): Promise<R> {
    if (!silent) {
      this.logger.info('Running ExtendScript:', script.substring(0, 2000))
    }
    script = `
      var value = undefined;
      try {
        value = {
          result: (
            function () {\n${script}\n}
          )() || null
        }
      } catch (error) {
        value = {
          error: error.toString(),
        }
      }
      app.JSON3.stringify(value)
    `
    return new Promise(resolve => {
      this.cs.evalScript(script, value => {
        try {
          if (value === 'undefined') {
            value = undefined
          } else {
            value = JSON.parse(value)
          }
        } catch (e) {
          console.error('ExtendScript result parse failed', e)
          console.error('Result:', value)
          console.error('Code:', script)
        }
        if (value.error) {
          this.logger.error('ExtendScript error:', value.error)
          throw new Error(value.error)
        }
        if (!silent) {
          this.logger.debug('Result:', value.result)
        }
        resolve(value.result as R)
      })
    })
  }

  hasRestart () {
    let result = false
    JSON.parse(window.__adobe_cep__.getExtensions()).forEach((ext) => {
      if (ext.id === 'com.elements.server') {
        result = true
      }
    })
    return (result as boolean) && !!this.extensionServerPort
  }

  restart () {
    this.logger.info('Restarting extension')
    location.href = `http://127.0.0.1:${this.extensionServerPort}/`
  }

  async open (item) {
    const type = item.asset?.type

    if (type === 'fcpxml' || (type === 'prproj' && !['AEFT', 'Resolve'].includes(this.app))) {
      if (!item.mainfile) {
        this.notify.error('Internal error: bundle mainfile unknown')
        return
      }
      if (type === 'fcpxml') {
        this.importEditorProject(item.mainfile.id)
      } else {
        this.importFile(item.mainfile.fullPath)
      }
    } else if (['video', 'audio', 'image'].includes(type)) {
      await this.importBundles([item]).catch(error => this.notify.error(error.toString()))
    }
  }

  async importSubclips (path, subclips: Subclip[]) {
    if (this.app === 'PPRO') {
      const items = subclips.map(item => {
        return { name: item.name, t_in: item.tIn, t_out: item.tOut }
      })

      this.evalScript(`app.premierePro.importSubclips(
        ${JSON.stringify(path)}, ${JSON.stringify(items)})`)
    }
  }

  importPath (path) {
    if (this.app === 'PPRO') {
      this.evalScript(`app.premierePro.importAssets([${JSON.stringify(path)}])`)
    }
    if (this.app === 'AEFT') {
      this.evalScript(`app.afterEffects.importAsset(${JSON.stringify(path)}, ${JSON.stringify(this.currentImportFolder)})`)
    }
  }

  override async importAsset (asset, path?: string) {
    if (this.app === 'AEFT') {
      if (!path) {
        const response = await this.locateItem({ asset: asset.id })
        path = response.path!
      }
      await this.importFile(path)
    } else {
      super.importAsset(asset)
    }
  }

  /**
  * Recursively imports bundles from the given directory structure.
  *
  * - For each directory, if it contains bundles, the import folder is updated and the bundles are imported.
  * - Empty directories are skipped.
  * - Restores original import folder after all operations.
  *
  **/
  async importDirs (dirs, folder?) {
    const importFolderBefore = await this.getImportFolder() ?? ''
    folder = folder ?? importFolderBefore
    try {
      for (const dir of dirs) {
        await loadAssetContentsPages(this.api, dir, async (bundles, directories) => {
          const importFolder = `${folder}/${dir.name}`

          if (bundles.length) {
            await this.setImportFolder(importFolder)
            await this.importBundles(bundles)
          }

          if (directories.length) {
            await this.importDirs(directories, importFolder)
          }
        })
      }
    } finally {
      await this.setImportFolder(importFolderBefore)
    }
  }

  override async importBundles (bundles) {
    if (this.app === 'AEFT') {
      for (const bundle of bundles) {
        if (['video', 'audio', 'image'].includes(bundle.asset.type)) {
          try {
            await this.importAsset(bundle.asset, bundle.mainfile.fullPath)
          } catch (error) {
            this.notify.error('Error while opening in After Effects', error)
          }
        }
      }
    } else {
      try {
        const subclips = await this.querySubclipImport(bundles)
        const importFolder = await this.getImportFolder()
        await super.importBundles(bundles)

        if (subclips.length) {
          for (const bundle of bundles) {
            const bundleSubclips = subclips.filter(item => item.asset.id === bundle.asset.id)
            if (!subclips.length) {
              continue
            }
            const url = await this.clientSideUrl.getPathUrl(bundle.mainfile.fullPath, this.platform)
            await this.setImportFolder(importFolder)
            await this.importSubclips(url, bundleSubclips)
          }
        }
      } catch (error) {
        this.notify.error('Error during the import to Premiere:', error)
      }
    }
  }

  getLocalPath (options) {
    const fs = this.getFS()
    const result = fs.showOpenDialog(
      false,
      options.properties.includes('openDirectory'),
      'Path',
      options.defaultPath,
    )
    if (result.data.length) {
      if (result.data[0].startsWith('file://')) {
        return decodeURI(result.data[0]).replace(/^(file:\/\/)/, '')
      } else {
        return result.data[0]
      }
    }
  }

  getConfig (key) {
    return window.localStorage.getItem(key)
  }

  override openURL (url) {
    this.cs.dispatchEvent({
      type: 'com.elements.extension.openURI',
      scope: 'APPLICATION',
      data: url,
      appId: undefined,
      extensionId: undefined,
    })
  }

  setConfig (key, value) {
    window.localStorage.setItem(key, value)
  }

  hasConfig () {
    return true
  }

  async getImportFolder (): Promise<string | null> {
    if (this.app === 'AEFT') {
      return this.currentImportFolder
    } else {
      return this.evalScript<string>(`return app.premierePro.getImportBin()`)
    }
  }

  setImportFolder (folder?) {
    if (this.app === 'AEFT') {
      this.currentImportFolder = folder
      return this.currentImportFolder
    } else {
      return this.evalScript(`return app.premierePro.setImportBin(${JSON.stringify(folder)})`)
    }
  }

  getActiveSequenceID (): Promise<string | null> {
    return this.evalScript<string>('return app.premierePro.getActiveSequenceID()')
  }

  async getActiveSequenceTimestamp (): Promise<Time | null> {
    return new Time(await this.evalScript<number>('return app.premierePro.getActiveSequenceTimestamp()'))
  }

  setActiveSequenceTimestamp (t: Time): Promise<void> {
    return this.evalScript(`app.premierePro.setActiveSequenceTimestamp(${t.valueOf()})`)
  }

  getActiveSequencePaths () {
    return this.evalScript<string[]>('return app.premierePro.getActiveSequenceFilePaths()')
  }

  getProjectPaths () {
    return this.evalScript<string[]>('return app.premierePro.getProjectFilePaths()')
  }

  attachProxies (pathProxyMap) {
    this.evalScript(`app.premierePro.attachProxies(${JSON.stringify(pathProxyMap)})`)
  }

  isProxyMode (): Promise<boolean> {
    return this.evalScript<boolean>('return app.getEnableProxies()', true)
  }

  setProxyMode (enabled) {
    return this.evalScript(`app.setEnableProxies(${JSON.stringify(enabled)})`, true)
  }

  override getMarkersForPath (path: string): Promise<EditorMarker[]> {
    return this.evalScript<EditorMarker[]>(`return app.premierePro.getMarkersForPath(${JSON.stringify(path)})`)
  }

  override async replaceMetadataForPath (path: string, markers: EditorMarker[], newTags: Tag[]): Promise<void> {
    const pathStr = JSON.stringify(path)
    this.evalScript(`app.premierePro.replaceMarkersForPath(${pathStr}, ${JSON.stringify(markers)})`)

    const logNoteValue = await this.evalScript<string | null>(`return app.premierePro.getLogNoteValue(${pathStr})`)
    const newTagsStr = newTags
      .map(t => `[${t.name}]`)
      .filter(t => !logNoteValue?.includes(t))
      .join(',')
    const newLogNote = [logNoteValue, newTagsStr].filter(Boolean).join(',')
    this.evalScript(`app.premierePro.setLogNoteValue(${pathStr}, ${JSON.stringify(newLogNote)})`)
  }

  override async renderSequence (
    ready: (_: GenericFileReader) => Promise<void>,
  ): Promise<void> {
    const fs = require('fs')
    const os = require('os')
    const path = require('path')

    const hasSequence = await this.evalScript<string>('return app.premierePro.hasActiveSequence()')
    if (!hasSequence) {
      throw new Error('No active sequence')
    }

    const modal = this.ngbModal.open(PremiereRenderModalComponent)
    modal.componentInstance.pickFile = () => {
      const result = this.getFS().showOpenDialog(false, false, 'Select an export preset', '', ['epr'])
      let picked: string | undefined = result.data[0]
      if (picked?.startsWith('file://')) {
        picked = decodeURI(picked).slice(7)
      }
      return Promise.resolve(picked)
    }
    const params: SequenceRenderParams | null = await modal.result.catch(() => null)

    if (!params) {
      return
    }

    const cepScope = {
      [SequenceRenderScope.Entire]: 'ENCODE_ENTIRE',
      [SequenceRenderScope.InOut]: 'ENCODE_IN_TO_OUT',
      [SequenceRenderScope.WorkArea]: 'ENCODE_WORKAREA',
    }[params.scope]

    const presetPath = path.join(os.tmpdir(), 'elements-sequence-export-preset.epr')
    fs.writeFileSync(presetPath, params.presetContent)

    const extension = await this.evalScript<string>(`return app.premierePro.getEncoderPresetExtension(${JSON.stringify(presetPath)})`)
    const outputPath = path.join(os.tmpdir(), 'elements-sequence-scratch.' + extension)

    await this.evalScript(`app.premierePro.renderActiveSequence(${JSON.stringify(outputPath)}, ${JSON.stringify(presetPath)}, ${JSON.stringify(cepScope)})`)

    try {
      const reader = new NodeFileReader(fs, outputPath, params.name + '.' + extension)
      try {
        await ready(reader)
      } finally {
        reader.close()
        fs.unlinkSync(outputPath)
      }
    } catch (e) {
      if (e.toString().includes('ENOENT')) {
        throw new Error('Render produced no file, is your sequence empty?')
      }
      throw e
    }
  }
}
